import { createStore, combineReducers, applyMiddleware, Middleware } from 'redux';
import { createLogger } from 'redux-logger';
import { datadogLogs } from '@datadog/browser-logs';
import LogRocket from 'logrocket';

import { getLogger } from '~Hooks';
import * as reducers from './reducers';
import * as actions from './actions';

const mutedLogs = ['NEW_TABLE_DATA', 'NEW_ORDER_STATUS_DATA', 'USER_PROVIDED_INPUT'];
const reduxLogger = createLogger({
  predicate: (getState, action) => Boolean(window.__DEV__) && !mutedLogs.includes(action.type),
  collapsed: (getState, action, logEntry) => !logEntry?.error,
});

/**
 * Logs to datadog from actions
 */

const printerLogger = getLogger('printer');
const eventCountLogger = getLogger('event-count');

// Temporary, verbose diagnostic logging requested to trace why table events (e.g. ADD_PAYMENT)
// sometimes never reach the backend for this company. Logs every event created and every
// sent/un-sent transition, across all of the many dispatch sites in useTableApi, from one place.
// Remove once the investigation into this company's missing events is resolved.
const DEBUG_COMPANY_ID = 'cmofdns2qqdxo0c22jqdz8i8s';
const tableEventDebugLogger = getLogger('table-events-debug');

const datdogLogger = (store: any) => (next: any) => (action: any) => {
  if (action.type === actions.MESSAGE_PRINT_BRIDGE_REQUEST_SUCCESS) {
    printerLogger.info('Print successful', {
      id: action.id,
    });
  }

  if (action.type === actions.MESSAGE_PRINT_BRIDGE_REQUEST_FAILED) {
    printerLogger.warn('Print failed', {
      id: action.id,
      error: action.error,
    });
  }

  if (action.type === actions.LOG_EVENT_COUNT) {
    const orderStatusEventCount: number | undefined = store.getState()?.orderStatusEvents?.length;
    if (orderStatusEventCount && orderStatusEventCount > 0) {
      eventCountLogger.info(`Order status event count: ${orderStatusEventCount}`);
    }
    const tableStatusEventCount: number | undefined = store.getState()?.tableEvents?.length;
    if (tableStatusEventCount && tableStatusEventCount > 0) {
      eventCountLogger.info(`Tables event count: ${tableStatusEventCount}`);
    }
  }

  if (action.type === actions.MESSAGE_PRINTERS) {
    datadogLogs.setGlobalContextProperty('printers', action.printers);
  }

  if (store.getState()?.company === DEBUG_COMPANY_ID) {
    if (action.type === 'ADD_TABLE_EVENT') {
      tableEventDebugLogger.info(`Table event created: ${action.event?.type}`, {
        eventId: action.event?.eventId,
        event: action.event,
      });
    }

    if (action.type === 'ADD_TABLE_EVENTS') {
      (action.events || []).forEach((event: any) => {
        tableEventDebugLogger.info(`Table event received from backend: ${event?.type}`, {
          eventId: event?.eventId,
          event,
        });
      });
    }

    if (action.type === 'MARK_EVENTS_SENT') {
      tableEventDebugLogger.info('Table events sent-state changed', {
        ids: action.ids,
        sentAt: action.sentAt,
        // null sentAt means these were just un-marked after a failed send attempt
        outcome: action.sentAt ? 'marked-sent' : 'un-marked-after-failure',
      });
    }
  }

  return next(action);
};

const reducer = combineReducers(reducers);
const store = createStore(
  reducer,
  {},
  applyMiddleware(reduxLogger, datdogLogger, LogRocket.reduxMiddleware()),
);
export type Store = typeof store;

export default store;
